import encryptionService from "./encryptionService";
import keyManager from "./keyManager";

/**
 * EncryptionInterceptor - Interceptor للتشفير التلقائي
 * يعترض الطلبات والردود لتطبيق التشفير تلقائياً
 */
class EncryptionInterceptor {
  constructor() {
    this.encryptionService = encryptionService;
    this.keyManager = keyManager;
    this.debug = Boolean(import.meta?.env?.DEV);
    // قائمة المسارات التي تحتاج تشفير (يمكن إضافة المزيد)
    // يمكن استخدام المسار النسبي (مثل '/auth/login') أو URL كامل
    this.encryptedPaths = [
      // Authentication
      '/auth/login',
      '/auth/refresh',
      '/save-fcm-token',
      
      // Dashboard
      '/dashboard/statistics',
      
      // Accounts
      '/profile',
      '/dashboard/accounts',
      '/dashboard/accounts/activate',
      '/dashboard/accounts/deactivate',
      '/dashboard/accounts/delete',
      
      // Emergencies
      '/dashboard/emergencies',
      
      // Finance
      '/dashboard/finance/wallets',
      '/dashboard/finance/transactions',
      
      // Advertisements
      '/dashboard/advertisements',
      
      // Employees/Admins
      '/dashboard/admins',
      
      // Rewards
      '/dashboard/rewards',
      '/dashboard/rewards/activate',
      '/dashboard/rewards/deactivate',
      '/dashboard/rewards/delete',
      '/dashboard/rewards/export',
      '/dashboard/rewards/import',
      
      // Support
      '/dashboard/support/tickets',
      '/dashboard/support/disputes',
      '/dashboard/support/messages',
      '/dispute-types',
      '/technical-support-categories',
      
      // Trips
      '/trip-infos',
      '/trips',
      
      // Vehicles
      '/dashboard/vehicles',
      '/cars',
      '/on-demand/categories',
      '/on-demand',
      '/dashboard/on-demand',
      
      // Configuration
      '/car-categories',
      '/car-colors',
      '/car-companies',
      '/car-models',
      '/currencies',
      '/fuel-types',
      '/services',
      '/insurance-providers',
      '/admin/on-demand',
      
      // Audit Logs
      '/dashboard/audit-logs',
      '/dashboard/audit-logs/export',
      
      // Settings
      '/emergency-numbers',
      '/policies',
      
      // Roles & Permissions
      '/dashboard/roles',
      '/dashboard/permissions',
      
      // Reports
      '/dashboard/reports',
      
      // Notifications
      '/admin/notifications',
      '/admin/targeted-notifications',
    ];
  }

  /**
   * تحويل المفاتيح من صيغة FormData (device[device_id]) إلى nested object
   * @param {Object} flatObject - Object بمفاتيح مسطحة
   * @returns {Object} Object ببنية متداخلة
   */
  convertFormDataKeysToNested(flatObject) {
    const nested = {};
    
    for (const [key, value] of Object.entries(flatObject)) {
      // التحقق من وجود صيغة array notation مثل device[device_id]
      if (key.includes('[') && key.includes(']')) {
        // استخراج الجزء الأساسي والجزء المتداخل
        // مثال: "device[device_id]" -> base: "device", nestedKey: "device_id"
        const match = key.match(/^([^\[]+)\[([^\]]+)\]$/);
        
        if (match) {
          const [, baseKey, nestedKey] = match;
          
          // إنشاء nested object إذا لم يكن موجوداً
          if (!nested[baseKey]) {
            nested[baseKey] = {};
          }
          
          // إضافة القيمة في المكان الصحيح
          nested[baseKey][nestedKey] = value;
        } else {
          // إذا لم تطابق الصيغة، نضيفها كما هي
          nested[key] = value;
        }
      } else {
        // إذا لم تكن بصيغة nested، نضيفها كما هي
        nested[key] = value;
      }
    }
    
    return nested;
  }

  /**
   * التحقق من الحاجة للتشفير
   * @param {Object} config - Axios config object
   * @returns {boolean} true إذا كان يحتاج تشفير
   */
  shouldEncrypt(config) {
    const headers = config.headers || {};
    const url = config.url || "";
    const baseURL = config.baseURL || "";

    // Explicit opt-out for endpoints that must send raw multipart/form-data
    if (
      headers["X-Skip-Encryption"] === "true" ||
      headers["x-skip-encryption"] === "true"
    ) {
      return false;
    }

    // التحقق من Header
    if (
      headers["X-Require-Encryption"] === "true" ||
      headers["x-require-encryption"] === "true"
    ) {
      return true;
    }

    // التحقق من القائمة البيضاء
    if (this.encryptedPaths.length > 0) {
      // بناء URL الكامل
      const fullUrl = baseURL && url ? `${baseURL}${url}` : url || baseURL;
      
      // التحقق من المسار النسبي أو URL الكامل
      const shouldEncrypt = this.encryptedPaths.some((path) => {
        // إزالة البروتوكول والـ domain للتحقق من المسار فقط
        const pathWithoutProtocol = path.replace(/^https?:\/\/[^\/]+/, "");
        const urlPath = url.startsWith("/") ? url : `/${url}`;
        
        // التحقق من URL الكامل أو المسار النسبي
        const matches = fullUrl.includes(path) || 
               urlPath === pathWithoutProtocol || 
               urlPath.includes(pathWithoutProtocol) ||
               url.includes(pathWithoutProtocol);
        
        return matches;
      });
      
      if (shouldEncrypt) {
        return true;
      }
    }

    // افتراضياً، لا نشفر إلا إذا كان هناك header صريح
    return false;
  }

  /**
   * إعداد Request Interceptor
   * @param {Object} axiosInstance - Axios instance
   */
  setupRequestInterceptor(axiosInstance) {
    axiosInstance.interceptors.request.use(
      async (config) => {
        // التحقق من الحاجة للتشفير
        const needsEncryption = this.shouldEncrypt(config);
        
        if (!needsEncryption) {
          return config;
        }

        try {
          // الحصول على مفتاح AES مشفر
          const encryptedAesKey =
            await this.encryptionService.getEncryptedAesKey();
          const publicKeyVersion =
            (await this.keyManager.getPublicKeyVersion()) || "";

          // إضافة Headers
          config.headers = config.headers || {};
          config.headers["x-key"] = encryptedAesKey;
          if (publicKeyVersion) {
            config.headers["x-key-version"] = publicKeyVersion;
          }

          // معالجة أنواع الطلبات المختلفة
          if (config.method === "get" || config.method === "GET") {
            // GET: فقط إضافة Headers (لا يوجد body)
            return config;
          }

          // POST/PUT/PATCH/DELETE: تشفير Body
          if (config.data) {
            if (config.data instanceof FormData) {
              // FormData: تشفير الحقول فقط (الحفاظ على الملفات)
              const fieldsMap = {};
              let methodField = null; // حفظ _method خارج التشفير
              
              for (const [key, value] of config.data.entries()) {
                if (!(value instanceof File)) {
                  // إخراج _method من التشفير (Laravel يحتاجه قبل فك التشفير)
                  if (key === "_method" || key === "method") {
                    methodField = { key, value };
                  } else {
                    fieldsMap[key] = value;
                  }
                }
              }

              // تحويل المفاتيح من صيغة device[device_id] إلى nested object
              const nestedFieldsMap = this.convertFormDataKeysToNested(fieldsMap);

              // تشفير الحقول (باستخدام البنية المتداخلة)
              const encryptedPayload =
                await this.encryptionService.encryptRequest(nestedFieldsMap);
              const encryptedString = encryptedPayload.encrypt;

              // إنشاء FormData جديد
              const encryptedFormData = new FormData();
              encryptedFormData.append("encrypt", encryptedString);

              // إضافة _method خارج التشفير (Laravel method spoofing)
              if (methodField) {
                encryptedFormData.append(methodField.key, methodField.value);
              }

              // إضافة الملفات كما هي
              for (const [key, value] of config.data.entries()) {
                if (value instanceof File) {
                  encryptedFormData.append(key, value);
                }
              }

              config.data = encryptedFormData;
              // إزالة Content-Type المحدد يدوياً للسماح لـ Axios بضبطه تلقائياً لـ FormData
              delete config.headers["Content-Type"];
            } else if (typeof config.data === "object") {
              // JSON: تشفير Body
              // إخراج _method من التشفير إذا كان موجوداً (لطلبات JSON مع method spoofing)
              let methodField = null;
              const dataToEncrypt = { ...config.data };
              
              if (dataToEncrypt._method) {
                methodField = dataToEncrypt._method;
                delete dataToEncrypt._method;
              } else if (dataToEncrypt.method) {
                methodField = dataToEncrypt.method;
                delete dataToEncrypt.method;
              }
              
              const encryptedPayload =
                await this.encryptionService.encryptRequest(dataToEncrypt);
              
              // إضافة _method خارج التشفير إذا كان موجوداً
              if (methodField) {
                config.data = {
                  ...encryptedPayload,
                  _method: methodField
                };
              } else {
                config.data = encryptedPayload;
              }
              
              config.headers["Content-Type"] = "application/json";
            }
          }
          
          return config;
        } catch (error) {
          // في حالة فشل التشفير، نرسل الطلب بدون تشفير
          // يمكن تغيير هذا السلوك حسب الحاجة
          return config;
        }
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * إعداد Response Interceptor
   * @param {Object} axiosInstance - Axios instance
   */
  setupResponseInterceptor(axiosInstance) {
    axiosInstance.interceptors.response.use(
      async (response) => {
        const requestConfig = response.config;

        // التحقق من الحاجة لفك التشفير
        if (!this.shouldEncrypt(requestConfig)) {
          return response;
        }

        try {
          // التحقق من وجود بيانات مشفرة
          if (response.data && typeof response.data === "object") {
            // الصيغة: { data: "Base64(Data+Tag).Base64(Nonce)" }
            if (
              response.data.data &&
              typeof response.data.data === "string"
            ) {
              // فك التشفير
              const decryptedData =
                await this.encryptionService.decryptResponse(response.data);

              // استبدال البيانات المشفرة بالبيانات المفكوكة
              response.data.data = decryptedData;
            }
          }

          return response;
        } catch (error) {
          // في حالة فشل فك التشفير، نعيد الرد كما هو
          // يمكن تغيير هذا السلوك حسب الحاجة
          return response;
        }
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * إعداد جميع Interceptors
   * @param {Object} axiosInstance - Axios instance
   */
  setup(axiosInstance) {
    this.setupRequestInterceptor(axiosInstance);
    this.setupResponseInterceptor(axiosInstance);
  }
}

// Export singleton instance
export const encryptionInterceptor = new EncryptionInterceptor();
export default encryptionInterceptor;
